import type { Scene } from "three";

/**
 * Which scene a part drag is happening in.
 *
 * `useDragStore` is a module singleton, so "a drag is running" is true for every
 * canvas in the page at once. Several things in the stage react to a drag by
 * drawing something cheaper — the shadow caster proxy stands in for the real
 * casters, the floor stops re-seating the product, post-processing drops to its
 * interacting quality. The preview and thumbnail canvases mount the same stage
 * under the same store (`previewCapture.tsx`, `positionPreview.tsx`), so reading
 * the singleton made them draw the editor's gesture: a preview captured while
 * the pointer was down got a bounding-box shadow and an unseated product, and
 * `previewCapture` stores that image. `endDrag` clears the flag 100 ms after
 * pointerup, so an ordinary save could land inside the window.
 *
 * Scoping it to the scene fixes the whole class: only the canvas that mounts
 * `ProductViewerDragScope` marks itself, and every other canvas reads false and
 * keeps drawing the settled image. A WeakMap for the same reason
 * `sceneActivity` and `studioFloorExtent` use one — the entry dies with the
 * scene.
 */
const dragging = new WeakMap<Scene, boolean>();

export function setSceneDragActive(scene: Scene, active: boolean): void {
  if (active) dragging.set(scene, true);
  else dragging.delete(scene);
}

export function isSceneDragActive(scene: Scene): boolean {
  return dragging.get(scene) === true;
}
